import {inject, Injectable} from "@angular/core"
import {ActivatedRoute} from "@angular/router"
import {CM_LOGGER_TEXTURES} from "@app/common/services/logging"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {RefreshService} from "@app/common/services/refresh/refresh.service"
import {ProcessingJobStateFragment, TextureEditorDataObjectFragment} from "@app/textures/service/textures-api.generated"
import {TexturesApiService} from "app/textures/service/textures-api.service"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {
    editsJsonHasUnsupportedOperator,
    getBakedMapDataObjectIds,
    getSourceMapDataObjectIds,
    TextureEditorCallback,
    TextureSetRevisionSpecific,
    TextureTypeData,
} from "@app/textures/texture-editor/texture-editor-callback"
import {TextureEditorDataService} from "@app/textures/texture-editor/texture-editor-data.service"
import {JobState, TextureType} from "@generated"

const MIN_SUPPORTED_SCHEMA_VERSION = TextureEditNodes.MIN_SUPPORTED_SCHEMA_VERSION

@Injectable()
export class TextureRevisionLoaderService {
    private readonly route = inject(ActivatedRoute)
    private readonly textureApi = inject(TexturesApiService)
    private readonly notification = inject(NotificationsService)
    private readonly refresh = inject(RefreshService)
    private readonly dataService = inject(TextureEditorDataService)
    private readonly logger = inject(CM_LOGGER_TEXTURES)

    // set synchronously when a load is requested (not when it completes), so the several route emissions that arrive
    // while a fetch is still in flight don't start overlapping loads of the same revision
    private lastRequested: {textureSetRevisionId: string; textureType: TextureType | undefined} | undefined

    async changeTexture(callback: TextureEditorCallback): Promise<void> {
        const textureSetRevisionId = this.route.snapshot.paramMap.get("textureSetRevisionId") as string
        const requestedTextureType = this.route.snapshot.queryParams.textureType as TextureType | undefined

        const revisionChanged = this.lastRequested?.textureSetRevisionId !== textureSetRevisionId
        if (!revisionChanged && this.lastRequested?.textureType === requestedTextureType) {
            // we have already loaded (or are loading) the correct texture
            return
        }
        this.lastRequested = {textureSetRevisionId, textureType: requestedTextureType}
        await this.loadRevisionAndSelectType(callback, revisionChanged ? textureSetRevisionId : undefined, requestedTextureType)
    }

    private async refreshInBackground(): Promise<void> {
        const revisionId = this.dataService.$revision()?.textureSetRevision.id
        if (!revisionId) {
            return
        }
        try {
            const {revision, editsProcessingJob} = await this.fetchRevision(revisionId)
            if (this.dataService.$revision()?.textureSetRevision.id !== revisionId) {
                return // user navigated to another revision while fetching
            }
            this.dataService.setRevision(revision)
            this.dataService.$editsProcessingJob.set(editsProcessingJob)
        } catch (error) {
            this.logger.warn("Background refresh of the texture set revision failed", error)
        }
    }

    async checkForProcessingEdits(): Promise<void> {
        const revision = this.dataService.$revision()
        if (!revision) {
            return
        }
        this.textureApi
            .queryTextureSetRevisionProcessingJobState(revision.textureSetRevision.id)
            .then((result) => result.textureSetRevision.editsProcessingJob)
            .then((editsProcessingJob) => {
                if (editsProcessingJob) {
                    const justCompleted = this.dataService.$editsProcessingJob()?.state !== JobState.Complete && editsProcessingJob.state === JobState.Complete
                    this.dataService.$editsProcessingJob.set(editsProcessingJob)
                    if (justCompleted) {
                        this.refresh.item({id: revision.textureSetRevision.id, __typename: "TextureSetRevision"})
                        void this.refreshInBackground()
                    }
                } else {
                    this.dataService.$editsProcessingJob.set(undefined)
                }
            })
            .catch((_error) => {
                this.dataService.$editsProcessingJob.set(undefined)
            })
    }

    // revisionIdToFetch: a concrete id forces a (re)fetch of that revision; undefined keeps the currently
    // loaded revision and just re-selects the texture type (instant, no network)
    private async loadRevisionAndSelectType(
        callback: TextureEditorCallback,
        revisionIdToFetch: string | undefined,
        requestedTextureType: TextureType | undefined,
    ): Promise<void> {
        const revisionChanged = revisionIdToFetch !== undefined
        callback.resetForNewLoad(revisionChanged)
        callback.$operatorStack()?.clearResultImageError()

        callback.setBusy(true)
        try {
            if (revisionChanged) {
                const {revision, editsProcessingJob} = await this.fetchRevision(revisionIdToFetch)
                if (this.lastRequested?.textureSetRevisionId !== revisionIdToFetch) {
                    // a newer load was requested while we were fetching
                    return
                }
                this.dataService.setRevision(revision)
                this.dataService.$editsProcessingJob.set(editsProcessingJob)
            }
            const revision = this.dataService.$revision()
            if (!revision) {
                return
            }

            // select texture type as requested or default to diffuse or first available
            const textureType =
                (requestedTextureType ? revision.availableTextureTypes.find((textureType) => textureType === requestedTextureType) : undefined) ??
                revision.availableTextureTypes.find((textureType) => textureType === TextureType.Diffuse) ??
                revision.availableTextureTypes[0]
            this.dataService.selectTextureType(textureType)

            if (!this.dataService.$displayDataObject()) {
                this.notification.showError("INTERNAL ERROR: No valid data found for the selected texture type.")
            }

            await this.checkForProcessingEdits()
        } catch (error) {
            this.dataService.setRevision(undefined)
            this.notification.showError("Error loading texture set revision: " + error)
        } finally {
            callback.setBusy(false)
        }
    }

    private async fetchRevision(
        textureSetRevisionId: string,
    ): Promise<{revision: TextureSetRevisionSpecific; editsProcessingJob: ProcessingJobStateFragment | null | undefined}> {
        const textureSetRevisionData = await this.textureApi.queryTextureSetRevision(textureSetRevisionId)
        const textureSetData = await this.textureApi.queryTextureSet(textureSetRevisionData.textureSet.id)
        const isLegacyUnsupportedEdit = editsJsonHasUnsupportedOperator(textureSetRevisionData.editsJson)
        if (!isLegacyUnsupportedEdit && textureSetRevisionData.editsJson) {
            if (textureSetRevisionData.editsJson.type !== "texture-edits" || textureSetRevisionData.editsJson.schema < MIN_SUPPORTED_SCHEMA_VERSION) {
                this.notification.showError("Unsupported texture edits schema. Ignoring edits.")
                textureSetRevisionData.editsJson = undefined
            }
        }
        const editsJson = isLegacyUnsupportedEdit ? undefined : (textureSetRevisionData.editsJson as TextureEditNodes.TextureEdits | undefined)
        const sortedTextureSetRevisionDescriptors = [...textureSetData.textureSetRevisions]
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
            .map((revision, index) => ({...revision, version: index + 1}))
            .reverse()
        const textureSetRevisionVersion = sortedTextureSetRevisionDescriptors.find((revision) => revision.id === textureSetRevisionId)!.version
        if (textureSetRevisionVersion == null) {
            throw Error("INTERNAL ERROR: Texture set revision version not found.")
        }
        const editsProcessingJob = await this.textureApi
            .queryTextureSetRevisionProcessingJobState(textureSetRevisionData.id)
            .then((result) => result.textureSetRevision.editsProcessingJob)
        const textureSetRevision = {...textureSetRevisionData, version: textureSetRevisionVersion}
        const sourceMapIds = getSourceMapDataObjectIds(textureSetRevision)
        const bakedMapIds = getBakedMapDataObjectIds(textureSetRevision)
        const availableTextureTypes = Array.from(sourceMapIds.keys()).filter((textureType) => bakedMapIds.has(textureType))
        const autoGenerateDisplacementMap = true
        if (autoGenerateDisplacementMap) {
            // in case we auto-generate displacement maps we can always show the resulting map even if no source map was given
            if (bakedMapIds.has(TextureType.Displacement) && !availableTextureTypes.includes(TextureType.Displacement)) {
                availableTextureTypes.push(TextureType.Displacement)
            }
        }

        // fetch every available type's source/result data objects up front, in parallel, so switching between
        // types afterward is instant. Without edits the source and result ids are the same, so each id is fetched once.
        const dataObjectById = new Map<string, Promise<TextureEditorDataObjectFragment>>()
        const fetchDataObject = (dataObjectId: string | undefined) => {
            if (!dataObjectId) {
                return Promise.resolve(undefined)
            }
            if (!dataObjectById.has(dataObjectId)) {
                dataObjectById.set(dataObjectId, this.textureApi.queryDataObject(dataObjectId))
            }
            return dataObjectById.get(dataObjectId)!
        }
        const typeDataEntries = await Promise.all(
            availableTextureTypes.map(async (textureType): Promise<[TextureType, TextureTypeData]> => {
                const [sourceDataObject, resultDataObject] = await Promise.all([
                    fetchDataObject(sourceMapIds.get(textureType)),
                    fetchDataObject(bakedMapIds.get(textureType)),
                ])
                const validResultDataObject = resultDataObject?.width && resultDataObject.height ? resultDataObject : undefined
                return [textureType, {sourceDataObject, resultDataObject: validResultDataObject}]
            }),
        )

        return {
            revision: {
                textureSetRevision,
                persistedTextureEdits: editsJson,
                availableTextureTypes,
                textureSet: textureSetData,
                sortedTextureSetRevisionDescriptors,
                typeData: new Map(typeDataEntries),
                autoGenerateDisplacementMap,
            },
            editsProcessingJob,
        }
    }
}
